import { Box, IconButton, Typography } from "@mui/material";
import { Icon } from "@platform/design-system";
import { logger } from "@platform/browser-logger";

import { isPlainObjectOrArray, nodeMatchesFilter } from "./jsonTreePaths";

const MUTED_TEXT_COLOR = "text.secondary";

interface JsonTreeProps {
  data: Record<string, unknown>;
  collapsedPaths: ReadonlySet<string>;
  onToggle: (path: string) => void;
  filter: string;
}

interface JsonTreeNodeProps {
  label: string;
  value: unknown;
  path: string;
  collapsedPaths: ReadonlySet<string>;
  onToggle: (path: string) => void;
  filter: string;
}

const copyToClipboard = (value: string): void => {
  if (!navigator.clipboard?.writeText) {
    return;
  }

  navigator.clipboard.writeText(value).catch((error: unknown) => {
    logger.error(
      "[EntityExplorer] Failed to copy value to clipboard",
      error instanceof Error ? error : undefined,
      { value },
    );
  });
};

const CopyButton = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element => (
  <IconButton
    size="small"
    aria-label={`Copy ${label}`}
    onClick={() => copyToClipboard(value)}
  >
    <Icon name="copy" size="sm" />
  </IconButton>
);

const LeafRow = ({
  label,
  value,
}: {
  label: string;
  value: unknown;
}): React.JSX.Element => {
  const display =
    value === null || value === undefined ? "null" : JSON.stringify(value);
  const color =
    value === null || value === undefined
      ? "text.disabled"
      : typeof value === "number"
        ? "info.main"
        : typeof value === "boolean"
          ? "warning.main"
          : "text.primary";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 1.5,
        py: 0.5,
        pl: 4,
        fontFamily: "monospace",
        fontSize: 13,
        "&:hover .copy-button": { opacity: 1 },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: "monospace",
          fontSize: 13,
          color: MUTED_TEXT_COLOR,
          minWidth: 190,
          flexShrink: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: "monospace",
          fontSize: 13,
          color,
          fontStyle:
            value === null || value === undefined ? "italic" : "normal",
          wordBreak: "break-word",
          flex: 1,
        }}
      >
        {display}
      </Typography>
      <Box className="copy-button" sx={{ opacity: 0, flexShrink: 0 }}>
        <CopyButton label={label} value={display} />
      </Box>
    </Box>
  );
};

const BranchRow = ({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}): React.JSX.Element => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      py: 0.5,
      cursor: "pointer",
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        transform: collapsed ? "rotate(-90deg)" : "none",
        color: MUTED_TEXT_COLOR,
      }}
    >
      <Icon name="expand" size="sm" />
    </Box>
    <Typography
      component="span"
      sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontFamily: "monospace",
        fontSize: 11,
        color: MUTED_TEXT_COLOR,
        bgcolor: "action.selected",
        px: 0.75,
        borderRadius: 4,
      }}
    >
      {count}
    </Typography>
  </Box>
);

const JsonTreeNode = ({
  label,
  value,
  path,
  collapsedPaths,
  onToggle,
  filter,
}: JsonTreeNodeProps): React.JSX.Element | null => {
  if (!nodeMatchesFilter(label, value, filter)) {
    return null;
  }

  if (!isPlainObjectOrArray(value)) {
    return <LeafRow label={label} value={value} />;
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value);
  const collapsed = filter ? false : collapsedPaths.has(path);

  return (
    <Box>
      <BranchRow
        label={label}
        count={entries.length}
        collapsed={collapsed}
        onToggle={() => onToggle(path)}
      />
      {!collapsed && (
        <Box
          sx={{
            ml: 2,
            pl: 1.5,
            borderLeft: "1px solid",
            borderColor: "divider",
          }}
        >
          {entries.map(([childLabel, childValue]) => (
            <JsonTreeNode
              key={childLabel}
              label={childLabel}
              value={childValue}
              path={`${path}.${childLabel}`}
              collapsedPaths={collapsedPaths}
              onToggle={onToggle}
              filter={filter}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export const JsonTree = ({
  data,
  collapsedPaths,
  onToggle,
  filter,
}: JsonTreeProps): React.JSX.Element => (
  <Box>
    {Object.entries(data).map(([label, value]) => (
      <JsonTreeNode
        key={label}
        label={label}
        value={value}
        path={label}
        collapsedPaths={collapsedPaths}
        onToggle={onToggle}
        filter={filter}
      />
    ))}
  </Box>
);
